import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/components/PersonalEntryDialog";
import { receiptShareText } from "@/components/ReceiptDialog";
import { toast } from "sonner";
import { FilePdf, WhatsappLogo, Envelope, Trash, Signature } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export function useReceipts(params) {
  const [data, setData] = useState({ items: [], contractors: [], total: 0 });
  const key = JSON.stringify(params);
  const reqId = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    const id = ++reqId.current; // ignore stale responses (e.g. initial "__none" resolving after the real viagem)
    return api.get("/receipts", { params }).then((r) => { if (id === reqId.current) setData(r.data); }).catch(() => {});
  }, [key]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export function ReceiptsList({ items, onChanged, showTrip = false }) {
  const whats = (r) => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(receiptShareText(r))}`, "_blank");
  const mail = (r) => { window.location.href = `mailto:?subject=${encodeURIComponent(`Recibo — Viagem ${r.viagem_number}`)}&body=${encodeURIComponent(receiptShareText(r))}`; };
  const del = async (r) => {
    if (!window.confirm("Excluir este recibo do histórico?")) return;
    await api.delete(`/receipts/${r.id}`); toast.success("Recibo excluído"); onChanged();
  };
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="receipts-empty">Nenhum recibo emitido ainda.</p>;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} data-testid={`receipt-row-${r.id}`} className="border rounded-md p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900">{r.contractor_name || "Contratante não informada"}{r.contractor_cnpj ? <span className="text-xs text-slate-500"> · CNPJ {r.contractor_cnpj}</span> : null}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
              {showTrip && <span>Viagem Nº {r.viagem_number} ·</span>}
              <span>{fmtDT(r.created_at)}</span>
              {r.signed && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Signature size={12} /> assinado</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-bold text-emerald-700 mr-2" data-testid={`receipt-value-${r.id}`}>{fmt(r.value)}</span>
            <Button size="icon" variant="ghost" asChild title="Baixar PDF"><a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" data-testid={`btn-receipt-download-${r.id}`}><FilePdf size={18} className="text-blue-600" /></a></Button>
            <Button size="icon" variant="ghost" onClick={() => whats(r)} title="Reenviar no WhatsApp" data-testid={`btn-receipt-resend-whatsapp-${r.id}`}><WhatsappLogo size={18} className="text-emerald-600" /></Button>
            <Button size="icon" variant="ghost" onClick={() => mail(r)} title="Reenviar por e-mail" data-testid={`btn-receipt-resend-email-${r.id}`}><Envelope size={18} className="text-slate-600" /></Button>
            <Button size="icon" variant="ghost" onClick={() => del(r)} title="Excluir" data-testid={`btn-receipt-delete-${r.id}`}><Trash size={16} className="text-red-600" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}
